import { UTCDate } from '@date-fns/utc'

/**
 * Copies calendar fields without applying the device timezone or its DST rules.
 * Use this for dates from date pickers and local view models, never for UTC instants.
 * @param {Date | string | number} date Calendar fields to copy.
 * @returns {UTCDate} A date whose getters, setters and date-fns operations preserve those fields.
 */
export function calendarDate(date: Date | string | number): UTCDate {
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}(?:T[\d:.]+)?$/.test(date)) {
    return new UTCDate(date.length === 10 ? `${date}T00:00:00Z` : `${date}Z`)
  }

  const source = isDateObject(date) ? date : new Date(date)

  return new UTCDate(
    source.getFullYear(),
    source.getMonth(),
    source.getDate(),
    source.getHours(),
    source.getMinutes(),
    source.getSeconds(),
    source.getMilliseconds()
  )
}

/**
 * Copies calendar fields into a native date in the device timezone. This is the reverse of `calendarDate`.
 * Use this where code compares or formats dates with device-local getters, such as chart day keys.
 * @param {Date} date Calendar fields to copy. UTCDate values keep their UTC fields; native dates keep their local fields.
 * @returns {Date} A native date with the same year, month, day and time fields.
 */
export function deviceCalendarDate(date: Date): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  )
}

/**
 * Checks for a Date object, including subclasses such as UTCDate.
 * Unlike `instanceof Date`, this also works when the global Date is replaced, for example by fake timers in specs.
 * @param {unknown} value The value to check.
 * @returns {value is Date} Whether the value is a Date object.
 */
export function isDateObject(value: unknown): value is Date {
  return Object.prototype.toString.call(value) === '[object Date]'
}
